import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  SelectChangeEvent,
  Typography,
  CircularProgress,
  Chip,
} from "@mui/material";
import { StorefrontRounded, Search, Store } from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import { useAppDispatch, useAppSelector } from "../../store/store";
import {
  selectCategory,
  selectFloor,
  selectSortBy,
  selectSearchQuery,
  setCategory,
  setFloor,
  setSortBy,
  setSearchQuery,
  resetFilters,
  selectHasFiltersApplied,
} from "../../store/slices/storesSlice";
import { StoreCategory } from "../../types/stores";
import { useGetAdminStoresQuery } from "../../features/stores/storesApiSlice";
import { useDebounce } from "../../hooks/useDebounce";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import {
  StoreCard,
  StoresFilterBar,
  StoresFilterBarSkeleton,
  StoresSkeleton,
} from "../../components/Stores";
import EmptyState from "../../components/Offers/EmptyState";
import { PAGINATION } from "../../config/constants";
// import { selectCurrentUser } from "../../store/slices/authSlice";
import HeroSection from "../../components/HeroSection";
import { gradients } from "../../utils/theme";

/**
 * AdminStores Screen
 *
 * Protected admin screen showing all stores in the mall admin's mall
 * with Point of Contact (POC) information for direct communication.
 *
 * Features:
 * - Reuses public StoreCard component with isAdminMode prop
 * - Shows POC details (name, designation, mobile, email, alternative mobile)
 * - Filtering by category, floor, search
 * - Sorting options
 * - Infinite scroll pagination
 * - Automatic mall filtering based on authenticated user
 * - Matches Dashboard/Analytics screen styling patterns
 */
const AdminStores: React.FC = () => {
  const dispatch = useAppDispatch();
  // const currentUser = useAppSelector(selectCurrentUser);
  const category = useAppSelector(selectCategory);
  const floor = useAppSelector(selectFloor);
  const sortBy = useAppSelector(selectSortBy);
  const searchQuery = useAppSelector(selectSearchQuery);
  const hasFilters = useAppSelector(selectHasFiltersApplied);

  const [currentPage, setCurrentPage] = useState(1);
  const [allLoadedStores, setAllLoadedStores] = useState<any[]>([]);

  // Debounce search query to avoid excessive API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 1000);

  // Fetch stores with filters and pagination
  const {
    data: storesData,
    isLoading: storesLoading,
    error: storesError,
    isFetching: storesFetching,
  } = useGetAdminStoresQuery({
    category: category !== "all" ? category : undefined,
    floor: floor !== "all" ? floor : undefined,
    sortBy,
    searchQuery: debouncedSearchQuery,
    page: currentPage,
    limit: PAGINATION.PAGE_LIMIT,
  });

  // Pagination info
  const pagination = storesData?.pagination;
  const hasMore = pagination ? pagination.page < pagination.totalPages : false;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    setAllLoadedStores([]);
  }, [category, floor, sortBy, debouncedSearchQuery]);

  // Accumulate stores when new page loads
  useEffect(() => {
    if (storesData?.stores !== undefined) {
      setAllLoadedStores((prev) => {
        // If it's page 1, replace all stores (filters changed)
        if (currentPage === 1) {
          return storesData.stores;
        }
        // Otherwise, append new stores
        const newStores = storesData.stores.filter(
          (newStore) =>
            !prev.some((existingStore) => existingStore.id === newStore.id),
        );
        return [...prev, ...newStores];
      });
    }
  }, [storesData, currentPage]);

  const stats = storesData?.stats;
  const totalStores = storesData?.total || 0;

  // Calculate floor count
  const floorCount = stats?.floors?.length || 0;

  // Load next page
  const loadMore = useCallback(() => {
    if (hasMore && !storesFetching) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasMore, storesFetching]);

  // Setup infinite scroll
  useInfiniteScroll(loadMore, hasMore, storesFetching);

  // Use accumulated stores
  const stores = allLoadedStores;

  const handleCategoryChange = (cat: StoreCategory | "all") => {
    dispatch(setCategory(cat));
  };

  const handleFloorChange = (event: SelectChangeEvent) => {
    dispatch(setFloor(event.target.value));
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    dispatch(
      setSortBy(event.target.value as "name" | "floor" | "offers" | "newest"),
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchQuery(e.target.value));
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  const handleClearSearch = () => {
    dispatch(setSearchQuery(""));
  };

  // Get available floors
  const availableFloors = stats?.floors || [];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: gradients.background,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Hero Section */}
      <HeroSection
        title="Stores Management"
        subtitle={`View all stores in your mall with direct access to store contacts • ${totalStores} ${totalStores === 1 ? "Store" : "Stores"} • ${floorCount} ${floorCount === 1 ? "Floor" : "Floors"}`}
        gradient="navyLux"
      />

      {/* Main Content */}
      <Box
        sx={{
          py: { xs: 3, sm: 4 },
          px: { xs: 2, sm: 2, md: 1.5 },
          position: "relative",
        }}
      >
        {/* Content Wrapper with Animation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <Box sx={{ position: "relative", minHeight: 600 }}>
            {/* Section Header */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 48,
                    height: 48,
                    borderRadius: 2.5,
                    background:
                      "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                    boxShadow: "0 6px 16px rgba(26, 35, 126, 0.3)",
                  }}
                >
                  <Store sx={{ fontSize: 24, color: "white" }} />
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: "text.primary",
                      letterSpacing: "-0.5px",
                      fontSize: { xs: "1.2rem", md: "1.4rem" },
                      mb: 0.5,
                    }}
                  >
                    Store Directory
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontWeight: 500 }}
                  >
                    View stores and their contacts
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Search & Filters Bar */}
            {storesLoading ? (
              <StoresFilterBarSkeleton />
            ) : (
              <StoresFilterBar
                showFilterBar={true}
                category={category}
                floor={floor}
                sortBy={sortBy}
                searchQuery={searchQuery}
                hasFilters={hasFilters}
                availableFloors={availableFloors}
                onCategoryChange={handleCategoryChange}
                onFloorChange={handleFloorChange}
                onSortChange={handleSortChange}
                onSearchChange={handleSearchChange}
                onResetFilters={handleResetFilters}
                onClearSearch={handleClearSearch}
              />
            )}

            {/* Stores Grid */}
            {storesLoading ? (
              <StoresSkeleton />
            ) : storesError ? (
              <EmptyState
                icon={<Search sx={{ fontSize: 80, color: "error.main" }} />}
                title="Error Loading Stores"
                description="Failed to load stores. Please try again."
              />
            ) : stores.length === 0 ? (
              <EmptyState
                icon={
                  <StorefrontRounded
                    sx={{ fontSize: 80, color: "text.secondary" }}
                  />
                }
                title="No Stores Found"
                description={
                  hasFilters
                    ? "Try adjusting your filters to see more results"
                    : "No stores available in your mall yet"
                }
                action={
                  hasFilters ? (
                    <Chip
                      label="Reset Filters"
                      onClick={handleResetFilters}
                      sx={{
                        background:
                          "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                        color: "white",
                        fontWeight: 600,
                        px: 3,
                        py: 3,
                        fontSize: "0.9rem",
                      }}
                    />
                  ) : undefined
                }
              />
            ) : (
              <Box>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <AnimatePresence mode="wait">
                    {stores.map((store, index) => (
                      <Grid item xs={12} sm={6} md={4} key={store.id}>
                        <StoreCard
                          store={store}
                          index={index}
                          isAdminMode={true}
                        />
                      </Grid>
                    ))}
                  </AnimatePresence>
                </Grid>

                {/* Loading indicator for infinite scroll */}
                {storesFetching && currentPage > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 4,
                      mt: 2,
                    }}
                  >
                    <CircularProgress size={40} />
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      Loading more stores...
                    </Typography>
                  </Box>
                )}

                {/* End of results message */}
                {!hasMore && stores.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: 4,
                      mt: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", fontStyle: "italic" }}
                    >
                      You've reached the end of the stores
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default AdminStores;
